import React from 'react';
import { Box, ButtonBase, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { GripVertical, Scan, ZoomIn, ZoomOut } from 'lucide-react';
import { Kbd } from '../../../components/ui';
import { focusRing, sbg, transition } from '../../../theme/sbgTokens';
import { formatCount, formatNumber, formatPercent } from '../../../utils/format';
import { DepartmentSwatch, NEUTRAL_SWATCH } from './orgChartModel';
import { DeptDot } from './OrgChartBits';

/** Floating zoom cluster in the canvas corner (a raised layer, so it may cast a shadow). */
export const ZoomControls: React.FC<{
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  onFit: () => void;
}> = ({ zoom, onZoomIn, onZoomOut, onReset, onFit }) => (
  <Box
    data-org-controls=""
    role="group"
    aria-label="Zoom"
    sx={{
      position: 'absolute',
      right: 16,
      bottom: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 0.25,
      p: 0.5,
      bgcolor: sbg.bg.raised,
      border: `1px solid ${sbg.border.default}`,
      borderRadius: sbg.radius.pill,
      boxShadow: sbg.shadow.md,
    }}
  >
    <Tooltip title="Zoom out">
      <IconButton size="small" aria-label="Zoom out" onClick={onZoomOut}>
        <ZoomOut />
      </IconButton>
    </Tooltip>
    <Tooltip title="Reset zoom to 100%">
      <ButtonBase
        onClick={onReset}
        aria-label={`Zoom ${formatPercent(zoom * 100)}. Reset to 100%`}
        sx={{
          minWidth: 52,
          height: 32,
          px: 1,
          borderRadius: sbg.radius.pill,
          color: sbg.text.primary,
          font: 'var(--sbg-type-label-sm)',
          fontVariantNumeric: 'tabular-nums',
          transition: transition('background-color'),
          '&:hover': { bgcolor: sbg.bg.hover },
          '&.Mui-focusVisible': focusRing,
        }}
      >
        {formatPercent(zoom * 100)}
      </ButtonBase>
    </Tooltip>
    <Tooltip title="Zoom in">
      <IconButton size="small" aria-label="Zoom in" onClick={onZoomIn}>
        <ZoomIn />
      </IconButton>
    </Tooltip>
    <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />
    <Tooltip title="Fit chart to screen">
      <IconButton size="small" aria-label="Fit chart to screen" onClick={onFit}>
        <Scan />
      </IconButton>
    </Tooltip>
  </Box>
);

/**
 * The chart's legend: one dot per department in its fixed series color, with
 * headcount. Each entry also filters the chart to that department.
 */
export const DepartmentLegend: React.FC<{
  swatches: DepartmentSwatch[];
  activeKey: string | null;
  onToggle: (key: string) => void;
}> = ({ swatches, activeKey, onToggle }) => {
  const items = swatches.filter((swatch) => swatch.colored || swatch.isNone);
  const others = swatches.filter((swatch) => !swatch.colored && !swatch.isNone);
  const otherCount = others.reduce((sum, swatch) => sum + swatch.count, 0);

  return (
    <Box component="ul" aria-label="Departments" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: -1 }}>
      {items.map((swatch) => {
        const pressed = activeKey === swatch.key;
        return (
          <Box component="li" key={swatch.key}>
            <Tooltip title={pressed ? 'Show all departments' : `Show only ${swatch.label}`}>
              <ButtonBase
                aria-pressed={pressed}
                onClick={() => onToggle(swatch.key)}
                sx={{
                  height: 28,
                  gap: 0.75,
                  px: 1,
                  borderRadius: sbg.radius.pill,
                  border: `1px solid ${pressed ? sbg.status.info.border : 'transparent'}`,
                  bgcolor: pressed ? sbg.status.info.bg : 'transparent',
                  color: pressed ? sbg.status.info.text : sbg.text.secondary,
                  font: 'var(--sbg-type-label-sm)',
                  whiteSpace: 'nowrap',
                  transition: transition('background-color', 'color', 'border-color'),
                  '&:hover': {
                    bgcolor: pressed ? sbg.bg.selectedStrong : sbg.bg.hover,
                    color: pressed ? sbg.status.info.text : sbg.text.primary,
                  },
                  '&.Mui-focusVisible': focusRing,
                }}
              >
                <DeptDot color={swatch.color} />
                {swatch.label}
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(swatch.count)}
                </Box>
              </ButtonBase>
            </Tooltip>
          </Box>
        );
      })}
      {otherCount > 0 && (
        <Box
          component="li"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 28,
            px: 1,
            color: sbg.text.secondary,
            font: 'var(--sbg-type-label-sm)',
            whiteSpace: 'nowrap',
          }}
        >
          <DeptDot color={NEUTRAL_SWATCH} />
          {formatCount(others.length, 'other department')}
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(otherCount)}</Box>
        </Box>
      )}
    </Box>
  );
};

/** How to move around the canvas with a pointer and the keyboard (and, for editors, how to re-parent someone). */
export const CanvasHint: React.FC<{ id: string; canEdit: boolean }> = ({ id, canEdit }) => (
  <Box id={id} sx={{ display: 'grid', gap: 0.5, maxWidth: { md: '60ch' } }}>
    <Typography
      variant="caption"
      component="p"
      sx={{ color: sbg.text.secondary, display: 'flex', alignItems: 'flex-start', gap: 0.75, m: 0 }}
    >
      {canEdit && (
        <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', pt: '1px', flex: 'none' }}>
          <GripVertical size={14} />
        </Box>
      )}
      <span>
        {canEdit ? 'Drag a person onto a manager to change who they report to. ' : ''}
        Drag the background to pan; hold Ctrl or ⌘ and scroll to zoom.
      </span>
    </Typography>
    <Typography
      variant="caption"
      component="p"
      sx={{ color: sbg.text.secondary, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.75, rowGap: 0.5, m: 0 }}
    >
      <span>Arrow keys move between people;</span>
      <Kbd>Enter</Kbd>
      <span>{canEdit ? 'opens details;' : 'opens details.'}</span>
      {canEdit && (
        <>
          <Kbd>E</Kbd>
          <span>edits placement.</span>
        </>
      )}
    </Typography>
  </Box>
);
